// Typed wrappers for /api/v2/admin/plans/*.
// Note: endpoints not yet in openapi.yaml; use shared adminFetch for CSRF/renew safety.
import { adminFetch, jsonRequest } from "@/lib/api/admin-fetch";

// Local types until /api/v2/admin/plans is added to openapi.yaml.
// Mirrors the backend `AdminPlanRowResponse` (services.subscriptions.plan_admin_store.AdminPlanRow)
// + `AdminSubscriptionPlanView` from quartel-bot-reversa.
export interface AdminPlanRow {
  id: string;
  displayName: string;
  description: string | null;
  kind: string;
  visibility: string;
  amountCents: number;
  checkoutUrl: string | null;
  periodDurationDays: number | null;
  tokensIncluded: number | null;
  isUnlimited: boolean;
  bypassDepositCheck: boolean;
  minDepositCents: number | null;
  robotLevel: number | null;
  aiAccessTier: string;
  defaultModelKey: string | null;
  isActive: boolean;
  // 10/07/2026: plano mais novo vira o default do "Liberar" nas pendências.
  createdAt?: string;
  // 0053: minutos entre o paid da Pagfy e a materialização da assinatura.
  releaseDelayMinutes?: number;
  sourceHints: unknown;
}
export type AdminPlanDetail = AdminPlanRow;
export interface AdminPlanWriteResult {
  id: string;
  displayName: string;
  kind: string;
  visibility: string;
  amountCents: number;
  isActive: boolean;
}
export interface PlanAnalyticsItem {
  planId: string;
  planName: string;
  activeSubscribers: number;
  totalRevenueCents: number;
  churnCount: number;
  conversionRate: number;
  activeCount: number;
  totalCount: number;
  matchedSalesCount: number;
  paidSalesCount: number;
  lastSaleAt: string | null;
  displayName: string;
  kind: string;
  amountCents: number;
  isActive: boolean;
}
export interface PlanAnalyticsResult {
  items: PlanAnalyticsItem[];
}
export interface SaleEvent {
  planId: string;
  userId: string;
  userEmail: string;
  amountCents: number;
  createdAt: string;
  provider: string;
  status: string;
  matchedBy: string;
  providerPaymentId: string | null;
  offerKey: string | null;
  offerLabel: string;
}
export interface PlanSalesPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}
export interface PlanSalesHistoryResult {
  items: SaleEvent[];
  pagination: PlanSalesPagination;
}
export interface UpsertPlanBody {
  id?: string;
  display_name?: string;
  description?: string | null;
  kind?: string;
  visibility?: string;
  amount_cents?: number;
  checkout_url?: string | null;
  period_duration_days?: number | null;
  tokens_included?: number | null;
  is_active?: boolean;
  grants_unlimited?: boolean;
  bypass_deposit_check?: boolean;
  min_deposit_cents?: number | null;
  robot_level?: number | null;
  ai_access_tier?: string;
  default_model_key?: string | null;
  release_delay_minutes?: number;
  source_hints?: unknown;
}
export interface PlanPatchBody {
  visibility?: string;
  isActive?: boolean;
  priceCents?: number;
  status?: "draft" | "published";
}

export type ListPlansParams = { limit?: number; offset?: number };
export type SalesHistoryParams = {
  limit?: number;
  offset?: number;
  provider?: string;
  status?: string;
  offer?: string;
};

export interface AdminPlanList {
  items: AdminPlanRow[];
  limit: number;
  offset: number;
}

export async function fetchAdminPlans(
  params: ListPlansParams,
): Promise<AdminPlanList> {
  const q = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  return adminFetch<AdminPlanList>(`/api/v2/admin/plans?${q}`);
}

export async function fetchAdminPlan(planId: string): Promise<AdminPlanRow> {
  return adminFetch<AdminPlanRow>(`/api/v2/admin/plans/${planId}`);
}

export async function fetchPlanAnalytics(): Promise<PlanAnalyticsResult> {
  return adminFetch<PlanAnalyticsResult>("/api/v2/admin/plans/analytics");
}

export async function fetchPlanSalesHistory(
  planId: string,
  params: SalesHistoryParams,
): Promise<PlanSalesHistoryResult> {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  if (params.provider) q.set("provider", params.provider);
  if (params.status) q.set("status", params.status);
  if (params.offer) q.set("offer", params.offer);
  return adminFetch<PlanSalesHistoryResult>(
    `/api/v2/admin/plans/${planId}/sales-history?${q}`,
  );
}

export async function createAdminPlan(
  body: UpsertPlanBody,
): Promise<AdminPlanWriteResult> {
  return adminFetch<AdminPlanWriteResult>(
    "/api/v2/admin/plans",
    jsonRequest("POST", body),
  );
}

export async function patchAdminPlan(
  planId: string,
  body: PlanPatchBody,
): Promise<AdminPlanWriteResult> {
  return adminFetch<AdminPlanWriteResult>(
    `/api/v2/admin/plans/${planId}`,
    jsonRequest("PATCH", body),
  );
}

// Sidebar badge — count of OPEN payment-pending entries.
// Backed by GET /api/v2/admin/payments/pending/count (BIG-1).
export interface AdminPendingCountResult {
  open: number;
}
export async function fetchAdminPendingCount(): Promise<AdminPendingCountResult> {
  return adminFetch<AdminPendingCountResult>(
    "/api/v2/admin/payments/pending/count",
  );
}
